import { useState } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ClipboardList, ArrowLeft, CheckCircle2, Clock, Plus, Search, Calendar, Users2, Star } from "lucide-react";
import { Link } from "react-router-dom";
import useAssessmentData from "../hooks/useAssessmentData";

/**
 * Assignments — derived from the author's REAL lesson submissions.
 *
 * This page rendered six invented assignments ("React Hooks Assignment",
 * 14/24 submissions, 78% average) that no API produced. There is no standalone
 * /assignments endpoint: submissions are created by learners against a lesson
 * and graded through PATCH …/submissions/:submissionId/grade. So each row here
 * is grouped by lesson and reflects genuine submission state.
 *
 * Source: GET  /api/resource-person/courses
 *         GET  /api/resource-person/courses/:id/submissions
 *         PATCH /api/resource-person/courses/:id/submissions/:submissionId/grade
 */
const metric = (value, suffix = "") =>
  value === null || value === undefined || Number.isNaN(Number(value)) ? "—" : `${value}${suffix}`;

const shortDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};


export default function AssignmentsPage() {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const {
    courses,
    coursesLoading,
    coursesError,
    selectedCourseId,
    setSelectedCourseId,
    submissions,
    submissionsLoading,
    submissionsError,
    courseStudents,
  } = useAssessmentData();

  // One row per lesson that actually has submissions. Grading happens in the
  // submissions inbox, so each row links straight there.
  const assignments = submissions.reduce((rows, submission) => {
    const key = submission.lessonId || submission.lessonTitle || "Lesson";
    const existing = rows.find((r) => r.key === key);
    const graded = submission.status === "GRADED" || submission.score != null;
    if (existing) {
      existing.count += 1;
      if (graded) existing.gradedCount += 1;
      const score = Number(submission.score);
      if (Number.isFinite(score)) {
        existing.scores.push(score);
        if (graded) existing.completed = true;
      }
    } else {
      const score = Number(submission.score);
      rows.push({
        key,
        id: key,
        name: submission.lessonTitle || "Lesson submission",
        description: submission.type ? `Type: ${submission.type}` : "Lesson submission",
        tags: [submission.type].filter(Boolean),
        count: 1,
        gradedCount: graded ? 1 : 0,
        completed: graded,
        scores: Number.isFinite(score) ? [score] : [],
        latestAt: submission.submittedAt || submission.createdAt || null,
      });
    }
    return rows;
  }, []);

  const filtered = assignments.filter((a) => {
    const matchesFilter = filter === "all" || (filter === "completed" ? a.completed : !a.completed);
    const matchesSearch = a.name.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Stats computed only from real submissions.
  const totalSubmissions = submissions.length;
  const completedCount = assignments.filter((a) => a.completed).length;
  const allScores = submissions.map((s) => Number(s.score)).filter((n) => Number.isFinite(n));
  const avgScore = allScores.length
    ? Math.round(allScores.reduce((s, n) => s + n, 0) / allScores.length)
    : null;

  const gradeHref = selectedCourseId
    ? `/resource-person/content-library/course/${selectedCourseId}/submissions`
    : "/resource-person/assessment";

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-4 gradient-primary rounded-2xl shadow-lg">
            <ClipboardList className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight mb-1">Assignments</h1>
            <p className="text-muted-foreground text-lg">Create and manage course assignments</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Link to="/resource-person/assessment">
            <Button variant="outline" size="lg" className="shadow-sm font-medium">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          </Link>
          {/* Submissions are created by learners against a lesson, so an "assignment"
              is really an assignment-type lesson — authored in the builder. */}
          {selectedCourseId ? (
            <Link to={`/resource-person/content-library/course/${selectedCourseId}/build`}>
              <Button size="lg" className="gradient-primary text-white shadow-md font-semibold">
                <Plus className="mr-2 h-4 w-4" /> New Assignment
              </Button>
            </Link>
          ) : (
            <Button size="lg" disabled className="font-semibold">
              <Plus className="mr-2 h-4 w-4" /> New Assignment
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Assignments", value: assignments.length, icon: ClipboardList, color: "text-primary" },
          { label: "Submissions", value: totalSubmissions, icon: Users2, color: "text-info" },
          { label: "Completed", value: completedCount, icon: CheckCircle2, color: "text-success" },
          { label: "Avg Score", value: metric(avgScore, "%"), icon: Star, color: "text-warning" }
        ].map((stat, i) => (
          <Card key={i} className="border-0 shadow-md">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">{stat.label}</p>
                <stat.icon className={`h-5 w-5 ${stat.color}`} />
              </div>
              <p className="text-3xl font-extrabold">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Course picker — submissions are course-scoped. */}
      <div className="flex flex-col gap-2 rounded-xl border bg-muted/30 p-4 sm:flex-row sm:items-center">
        <label className="text-sm font-semibold" htmlFor="assign-course">Course</label>
        <select
          id="assign-course"
          className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm"
          value={selectedCourseId}
          disabled={coursesLoading || !courses.length}
          onChange={(e) => setSelectedCourseId(e.target.value)}
        >
          {courses.length ? (
            courses.map((c) => (
              <option key={c.id} value={c.id}>{c.title || "Untitled course"}</option>
            ))
          ) : (
            <option value="">{coursesLoading ? "Loading courses…" : "No courses yet"}</option>
          )}
        </select>
      </div>

      {coursesError ? (
        <p className="m-0 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{coursesError}</p>
      ) : null}
      {submissionsError ? (
        <p className="m-0 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{submissionsError}</p>
      ) : null}

      {/* Filters + Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Search assignments or courses..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {["all", "pending", "completed"].map(f => (
            <Button
              key={f}
              variant={filter === f ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f ? "gradient-primary text-white" : ""}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Assignment Cards */}
      <div className="grid gap-4">
        {!submissionsLoading && !filtered.length ? (
          <Card className="border-0 shadow-md">
            <CardContent className="p-10 text-center">
              <ClipboardList className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
              <p className="mb-1 font-semibold">
                {selectedCourseId ? "No submissions in this course" : "Choose a course to see its submissions"}
              </p>
              <p className="m-0 text-sm text-muted-foreground">
                {selectedCourseId
                  ? "Submissions appear here as learners submit work against your lessons."
                  : "Submissions are listed per course."}
              </p>
            </CardContent>
          </Card>
        ) : null}
        {filtered.map(assignment => (
          <Card key={assignment.id} className="border-0 shadow-md hover:shadow-xl transition-all duration-200">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-bold">{assignment.name}</h3>
                    {assignment.completed ? (
                      <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        <CheckCircle2 className="h-3 w-3" /> Completed
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        <Clock className="h-3 w-3" /> Pending
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground">{assignment.description}</p>

                  <div className="flex flex-wrap gap-2">
                    {assignment.tags.map(tag => (
                      <span key={tag} className="text-xs px-2 py-0.5 bg-muted rounded-md text-muted-foreground font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span className="font-medium">{courseStudents.length} enrolled</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {assignment.completed ? "Completed" : `Last submitted ${shortDate(assignment.latestAt)}`}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>
                        {assignment.gradedCount}/{assignment.count} graded
                      </span>
                      {assignment.scores.length ? (
                        <span className="font-semibold text-success">
                          Avg: {Math.round(assignment.scores.reduce((s, n) => s + n, 0) / assignment.scores.length)}%
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="flex md:flex-col gap-2 md:w-32">
                  <Link to={gradeHref}>
                    <Button size="sm" className="w-full gradient-primary text-white text-xs">
                      View Details
                    </Button>
                  </Link>
                  {!assignment.completed && (
                    <Link to={gradeHref}>
                      <Button size="sm" variant="outline" className="w-full text-xs">
                        Grade
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}